
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const team = [
  {
    name: 'Janusz Gładysz',
    role: 'Właściciel / Dyrektor',
    description:
      'Założyciel firmy z ponad 35-letnim doświadczeniem w branży konstrukcji stalowych i zarządzaniu przedsiębiorstwem.',
    phone: '+48 600 978 050',
    email: 'jgladysz@eureka.sanok.pl',
    icon: 'ri-user-star-line',
  },
  {
    name: 'Konrad Komarski',
    role: 'Kierownik Produkcji',
    description:
      'Odpowiada za planowanie i nadzór nad procesami produkcyjnymi, kontrolę jakości oraz koordynację zespołu.',
    phone: '+48 790 788 033',
    email: 'kkomarski@eureka.sanok.pl',
    icon: 'ri-settings-4-line',
  },
];

export default function TeamSection() {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 bg-[#050505] overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span
            className="text-[#00E5FF] text-xs tracking-[0.3em] uppercase font-semibold"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Kierownictwo
          </span>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-4 leading-tight"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Nasz <span className="text-[#00E5FF]">Zespół</span>
          </h2>
          <p
            className="text-[#C0C7CF] text-sm sm:text-base mt-4 max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Doświadczona kadra zarządzająca i zespół 19 wykwalifikowanych
            specjalistów
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
          {team.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.15 }}
              className="group bg-[#111111] border border-[#1A1A1A] p-6 sm:p-8 hover:border-[#00E5FF]/40 transition-all duration-500"
            >
              <div className="flex items-start gap-5">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center bg-[#00E5FF]/5 border border-[#00E5FF]/20 flex-shrink-0 group-hover:bg-[#00E5FF]/10 group-hover:border-[#00E5FF]/40 transition-all duration-500">
                  <i
                    className={`${member.icon} text-3xl sm:text-4xl text-[#00E5FF]`}
                  ></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className="text-lg sm:text-xl font-bold text-white leading-tight"
                    style={{ fontFamily: 'Rajdhani, sans-serif' }}
                  >
                    {member.name}
                  </h3>
                  <span
                    className="text-[#00E5FF] text-[10px] sm:text-xs tracking-wider uppercase font-semibold"
                    style={{ fontFamily: 'Orbitron, sans-serif' }}
                  >
                    {member.role}
                  </span>
                </div>
              </div>

              <p
                className="text-[#888] text-sm leading-relaxed mt-5 group-hover:text-[#C0C7CF] transition-colors duration-300"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {member.description}
              </p>

              <div className="mt-5 pt-5 border-t border-[#1A1A1A] flex flex-col sm:flex-row gap-3 sm:gap-6">
                <a
                  href={`tel:${member.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-2 text-[#C0C7CF] hover:text-[#00E5FF] transition-colors duration-300 text-sm cursor-pointer"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <i className="ri-phone-line text-[#00E5FF] text-base"></i>
                  {member.phone}
                </a>
                <a
                  href={`mailto:${member.email}`}
                  className="flex items-center gap-2 text-[#C0C7CF] hover:text-[#00E5FF] transition-colors duration-300 text-sm cursor-pointer truncate"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <i className="ri-mail-line text-[#00E5FF] text-base flex-shrink-0"></i>
                  <span className="truncate">{member.email}</span>
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Team stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 sm:mt-16 bg-[#111111] border border-[#00E5FF]/10 p-6 sm:p-8"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
            {[
              { value: '19', label: 'Pracowników' },
              { value: '8', label: 'Certyfikowanych spawaczy' },
              { value: '5', label: 'Inżynierów' },
              { value: '35+', label: 'Lat doświadczenia' },
            ].map((stat, i) => (
              <div key={i}>
                <div
                  className="text-2xl sm:text-3xl font-bold text-[#00E5FF]"
                  style={{ fontFamily: 'Orbitron, sans-serif' }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-[#C0C7CF] text-[10px] sm:text-xs tracking-wider uppercase mt-1"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
